import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePrismAuth } from '@/lib/prism-bridge';

/**
 * Refetch data queries when Prism pushes a *refreshed* OptiID token.
 *
 * On reload the first `prismContext` can carry a stale/near-expired token: the apps query fires
 * with it, rivendell returns 401, and since 4xx isn't retried (see queryClient) the error page
 * sticks — even after Prism sends a fresh token via `optiIdTokenUpdated` a moment later, because
 * the token isn't part of any query key so nothing refetches. Invalidating on a token *change*
 * makes React Query re-run each query's current queryFn, which closes over the new token.
 *
 * Only reacts to a change from one non-null token to a different one: the initial null -> token
 * transition is left to each query's own `enabled` gating, so this adds no duplicate first fetch.
 * Must render inside both PrismBridgeProvider (for usePrismAuth) and QueryClientProvider.
 */
export function AuthRefetchSync() {
  const { token } = usePrismAuth();
  const queryClient = useQueryClient();
  const previousToken = useRef(token);

  useEffect(() => {
    const changed = token && previousToken.current && token !== previousToken.current;
    previousToken.current = token;
    if (changed) {
      void queryClient.invalidateQueries();
    }
  }, [token, queryClient]);

  return null;
}
